import * as ms from 'mousetrap';
import { DependencyList, useEffect, useMemo } from 'react';
import { atom, useRecoilState } from 'recoil';
import { useAuthState } from 'state/useAuthState';
import { usePostHog } from 'posthog-js/react';
import { isProductionEnvironment } from 'shared/envUtils';
import { CommandPaletteAction } from 'components/CommandPaletteDialog/commandPaletteAction.types';

const registeredShortcutStatAtom = atom<CommandPaletteAction[]>({
  key: 'REGISTERED_SHORTCUTS',
  default: [],
});

export const useRegisteredShortcuts = (): CommandPaletteAction[] => {
  const [registeredShortcuts] = useRecoilState(registeredShortcutStatAtom);
  return [...registeredShortcuts];
};

export const useKeyboardShortcuts = (
  shortcuts: CommandPaletteAction[],
  deps: DependencyList = [],
): void => {
  const {
    permissions: { isInternalUser },
  } = useAuthState();
  const posthog = usePostHog();
  const [, setRegisteredShortcuts] = useRecoilState(registeredShortcutStatAtom);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedShortcuts = useMemo(() => shortcuts, deps);

  useEffect(() => {
    if (
      (isProductionEnvironment() && !isInternalUser) ||
      memoizedShortcuts.length === 0
    ) {
      return () => {};
    }

    setRegisteredShortcuts((prevRegistry) => [
      ...prevRegistry,
      ...memoizedShortcuts,
    ]);

    // Command-palette-only entries have no keys; only bind the ones that do.
    const boundShortcuts = memoizedShortcuts.filter(({ keys }) => keys);

    boundShortcuts.forEach(({ keys, description, handler }) => {
      ms.bind(keys!, (...args) => {
        posthog.capture('keyboardShortcut', { description });
        handler(...args);
      });
    });

    return () => {
      setRegisteredShortcuts((prevRegistry) =>
        prevRegistry.filter(
          (shortcut) =>
            !memoizedShortcuts.some(
              (s) =>
                s.keys === shortcut.keys &&
                s.description === shortcut.description,
            ),
        ),
      );

      boundShortcuts.forEach(({ keys }) => {
        ms.unbind(keys!);
      });
    };
  }, [memoizedShortcuts, isInternalUser, setRegisteredShortcuts, posthog]);
};
